// Global
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { ThemeName, useTheme } from 'lib/context/ThemeContext';
import { withDatasourceCheck, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import Link from 'next/link';
import classNames from 'classnames';

// Components
import { ProductPreviewCardTheme } from './ProductPreviewCard.theme';
import Headline from 'src/helpers/Headline/Headline';
import { BodyCopy } from 'src/helpers/BodyCopy';
import { Eyebrow } from 'src/helpers/Eyebrow';
import { Button } from 'src/helpers/Button';
import Image from 'src/helpers/Media/Image';
import SvgIcon from 'src/helpers/SvgIcon/SvgIcon';
import { EnumField } from 'lib/utils/get-enum';
import { RichTextWrapper } from 'src/helpers/RichTextWrapper';
import SingleButton from 'src/helpers/SingleButton/SingleButton';
import PriceLevel from 'src/helpers/PriceLevel/PriceLevel';
import { useBVScript } from 'lib/utils/use-bv-script';
import ImagePrimary from 'src/helpers/Media/ImagePrimary';
import { useReviewSummary, parseFilterRuleDataForAggregates } from 'lib/reviews/get-reviews-v2';
import { StarRating } from 'src/helpers/StarRating';
import { useAffiliate } from 'lib/context/AffiliateContext';
import { useState } from 'react';

type priceLevel = 1 | 2 | 3 | 4 | 5;

type ProductItem = Feature.EnterpriseWeb.Enterprise.Data.Products.EnterpriseProduct & {
  fields?: {
    standardExteriorColors?: Feature.EnterpriseWeb.Enterprise.Elements.Swatches.SwatchCollection & {
      fields?: {
        swatches: Feature.EnterpriseWeb.Enterprise.Elements.Swatches.Swatch[];
      };
    };
    priceLevel: EnumField<priceLevel>;
    featuredExteriorColors: Feature.EnterpriseWeb.Enterprise.Elements.Swatches.Swatch[];
  };
};

export type ProductPreviewCardProps = Feature.EnterpriseWeb.Enterprise.Cards.ProductPreviewCard & {
  fields?: {
    productItem: ProductItem;
  };
};

const ProductPreviewCard = (props: ProductPreviewCardProps) => {
  const { fields } = props;
  const { themeName, themeData } = useTheme(ProductPreviewCardTheme);
  const favoriteProductsArr = props.favoriteProducts || [];
  const { userAffiliate } = useAffiliate();
  const reviewModCategory: string[] = fields?.productItem?.fields?.reviewModCategory?.map(
    (review: any) => {
      return review?.fields?.Value.value;
    }
  );
  const affiliateId =
    userAffiliate?.affiliateId && userAffiliate.affiliateId !== '0'
      ? [userAffiliate.affiliateId]
      : undefined;
  const filters = parseFilterRuleDataForAggregates(reviewModCategory, affiliateId);
  const productReview = useReviewSummary(filters);
  const [selectedSwatchImage, setSelectedSwatchImage] = useState<null | {
    src: string;
    alt?: string;
    width?: string;
    height?: string;
  }>(null);

  // Add the bazaarvoice script
  useBVScript({ environment: process.env.NEXT_PUBLIC_EW_ENVIRONMENT, theme: themeName });

  if (!fields) {
    return null;
  }

  const bazaarvoiceProductId = fields?.productItem?.fields?.bazaarvoiceProductId?.value;

  const showFavorite = fields?.favorite?.value;
  const productID = fields.productItem?.fields?.productId?.value;
  const isFavorited = favoriteProductsArr.includes(productID);

  const colorSwatchesAll = fields.productItem?.fields?.standardExteriorColors?.fields?.swatches;
  const colorSwatchesTotal = colorSwatchesAll && colorSwatchesAll.length;

  let featuredExteriorColors = fields?.productItem?.fields?.featuredExteriorColors || [];

  let colorSwatchesCount = 0;
  function pairClickablesWithRenderedImages(props: any) {
    const productFields = props?.fields?.productItem?.fields;

    if (!productFields) {
      return [];
    }

    const {
      clickableSwatch1,
      clickableSwatch2,
      clickableSwatch3,
      renderedImage1,
      renderedImage2,
      renderedImage3,
    } = productFields;

    const clickables = [clickableSwatch1, clickableSwatch2, clickableSwatch3];
    const rendereds = [renderedImage1, renderedImage2, renderedImage3];

    const result = clickables
      .map((clickable, index) => {
        const rendered = rendereds[index];
        if (clickable && rendered) {
          return { clickable, rendered };
        }
        return null;
      })
      .filter(Boolean);

    return result;
  }
  const pairedClickables = pairClickablesWithRenderedImages(props);

  const MAX_FEATURE_EXTERIOR_COLORS: Record<ThemeName, number> = {
    aw: 3,
    rba: 5,
  };

  if (colorSwatchesTotal && featuredExteriorColors?.length > 0) {
    const maxColors = MAX_FEATURE_EXTERIOR_COLORS[themeName];
    featuredExteriorColors = featuredExteriorColors.slice(0, maxColors);
    colorSwatchesCount = colorSwatchesTotal - featuredExteriorColors.length;
  }

  const priceLevel =
    fields?.productItem?.fields?.priceLevel?.fields?.priceLevelText?.value &&
    (+fields?.productItem?.fields?.priceLevel?.fields?.priceLevelText?.value as priceLevel | null);

  const ColorAndActions = (): JSX.Element => {
    return (
      <>
        {featuredExteriorColors?.length > 0 && (
          <div className={themeData.classes.colorSwatchesWrapper}>
            <Text
              tag={'h4'}
              field={{ value: fields?.interiorColorLabel?.value }}
              className={themeData.classes.colorLabel}
            />
            <Link
              href={
                fields.cta1Link.value.href ||
                fields.productItem?.fields?.productDetailPageLink?.value?.href ||
                ''
              }
            >
              <a
                {...(pairedClickables.length > 0 && {
                  onClick: (e) => {
                    e.preventDefault();
                  },
                })}
              >
                {pairedClickables.length > 0 ? (
                  <div className={themeData.classes.swatches}>
                    {pairedClickables?.map(
                      (
                        // @ts-ignore since these are new fields
                        color: any,
                        index: number
                      ) => {
                        const swatchImage = color?.clickable.fields?.swatchImage?.value;
                        const clickableSwatch = color?.rendered?.value;
                        const isSelected = clickableSwatch === selectedSwatchImage;
                        const hasValidImage = swatchImage && Object.keys(swatchImage).length > 0;
                        return hasValidImage ? (
                          <div
                            key={index}
                            className={classNames(
                              themeData.classes.colorSwatches,
                              isSelected
                                ? 'rounded-full p-[2px] ring-2 ring-black'
                                : 'opacity-80 hover:opacity-100'
                            )}
                            onClick={() => setSelectedSwatchImage(clickableSwatch)}
                          >
                            <Image image={color?.clickable.fields?.swatchImage} />
                          </div>
                        ) : null;
                      }
                    )}
                    {colorSwatchesCount > 0 && <div>+{colorSwatchesCount}</div>}
                  </div>
                ) : (
                  <div className={themeData.classes.swatches}>
                    {featuredExteriorColors?.map(
                      (
                        color: Feature.EnterpriseWeb.Enterprise.Elements.Swatches.Swatch,
                        index: number
                      ) => {
                        return (
                          <div key={index} className={themeData.classes.colorSwatches}>
                            <Image image={color.fields?.swatchImage} />
                          </div>
                        );
                      }
                    )}
                    {colorSwatchesCount > 0 && <div>+{colorSwatchesCount}</div>}
                  </div>
                )}
              </a>
            </Link>
          </div>
        )}
        <div className={themeData.classes.actions}>
          {fields.cta1Link?.value?.href && fields.cta1Link?.value?.text ? (
            <div className={themeData.classes.buttonWrapper}>
              <Button
                field={fields?.cta1Link}
                variant={fields?.cta1Style}
                icon={fields?.cta1Icon}
                modalId={
                  (
                    fields?.cta1Modal as unknown as Feature.EnterpriseWeb.Enterprise.Components.Modal.GenericModal.GenericModal
                  )?.fields?.modalId?.value
                }
                modalLinkText={fields?.cta1ModalLinkText}
                classes={classNames(
                  fields?.cta1Style,
                  themeData.classes.buttonGroupClass.cta1Classes
                )}
              ></Button>
              <Button
                field={fields?.cta2Link}
                variant={fields?.cta2Style}
                icon={fields?.cta2Icon}
                modalId={
                  (
                    fields?.cta2Modal as unknown as Feature.EnterpriseWeb.Enterprise.Components.Modal.GenericModal.GenericModal
                  )?.fields?.modalId?.value
                }
                modalLinkText={fields?.ctaModal2LinkText}
                classes={classNames(
                  fields?.cta2Style,
                  themeData.classes.buttonGroupClass.cta2Classes
                )}
              ></Button>
            </div>
          ) : (
            fields.productItem?.fields.productDetailPageLink?.value?.text && (
              <SingleButton
                classes={
                  (themeData.classes.buttonGroupClass,
                  { wrapper: 'mb-0 md:mb-s cursor-pointer', cta1Classes: 'font-bold' })
                }
                fields={{
                  cta1Link: {
                    value: {
                      href: fields.productItem.fields.productDetailPageLink.value.href,
                      text: fields.productItem.fields.productDetailPageLink.value.text,
                      anchor: fields.productItem.fields.productDetailPageLink.value.anchor,
                      target: fields.productItem.fields.productDetailPageLink.value.target,
                    },
                  },
                  cta1AriaLabel: {
                    value: '',
                  },
                  cta1ModalLinkText: {
                    value: '',
                  },
                  cta1Style: {
                    id: '',
                    url: '',
                    name: 'Primary',
                    displayName: 'Primary',
                    fields: {
                      Value: {
                        value: fields.cta1Style?.fields?.Value?.value || 'primary',
                      },
                    },
                    templateId: '',
                    templateName: 'Enum',
                  },
                  cta1Icon: {
                    id: '',
                    url: '',
                    name: 'Arrow',
                    displayName: 'Arrow',
                    fields: {
                      Value: {
                        value: 'arrow',
                      },
                    },
                    templateId: '',
                    templateName: 'Enum',
                  },
                }}
              />
            )
          )}
        </div>
      </>
    );
  };

  let primaryImageVal = {};
  let primaryImageMobileVal = {};

  const primaryImage = fields.primaryImage?.value?.src && fields.primaryImage.value;
  const primaryImageMobile =
    fields.primaryImageMobile?.value?.src && fields.primaryImageMobile?.value;
  const productImage = fields.productItem?.fields?.productImage?.value;
  const productImageMobile = fields.productItem?.fields?.productImageMobile?.value;

  primaryImageVal = primaryImage || productImage || {};
  primaryImageMobileVal = primaryImageMobile || productImageMobile || primaryImageVal;

  const averageRating = productReview?.averageRating || '';

  return (
    <div
      className={themeData.classes.productPreviewCradWrapper}
      data-component="card/productpreview"
    >
      {/* Favourite */}
      {showFavorite && productID && (
        <div className="absolute top-0 right-0">
          <div
            className={classNames(
              themeData.classes.favoriteProduct,
              isFavorited ? 'favorited border-[transparent_#f26924_transparent_transparent]' : ''
            )}
            data-product-id={productID}
          >
            <SvgIcon
              icon="favorite"
              fillId="white"
              size="xl"
              className={themeData.classes.favoriteIcon}
            />
          </div>
        </div>
      )}

      <div className={themeData.classes.headerWrapper}>
        {fields.eyebrowText?.value ? (
          <Eyebrow classes={themeData.classes.eyebrow} {...props} />
        ) : (
          fields.productItem?.fields.productSeries &&
          fields.productItem?.fields?.productSeries?.fields?.productTypeName?.value && (
            <Text
              tag="h4"
              className={themeData.classes.eyebrow}
              field={{
                value: fields.productItem.fields.productSeries.fields.productTypeName.value,
              }}
            />
          )
        )}

        <Link
          href={
            fields.cta1Link.value.href ||
            fields.productItem?.fields?.productDetailPageLink?.value?.href ||
            ''
          }
        >
          <a className={themeData.classes.headlineWrapper}>
            {fields.headlineText?.value ? (
              <Headline useTag="h4" classes={themeData.classes.headline} {...props} />
            ) : (
              fields.productItem?.fields.productName && (
                <Text
                  useTag="h4"
                  className={themeData.classes.headline}
                  field={{ value: fields.productItem.fields.productName.value }}
                />
              )
            )}
          </a>
        </Link>

        {/* ratings */}
        <div className={themeData.classes.ratingsAndPriceWrapper}>
          {bazaarvoiceProductId && themeName === 'aw' && (
            <div
              className="review flex-[0_0_auto]"
              data-bv-show="inline_rating"
              data-bv-product-id={bazaarvoiceProductId}
              data-bv-seo="false"
            ></div>
          )}
          {themeName === 'rba' && averageRating && (
            <>
              {/* Ratings icons RbA */}
              <div className={themeData.classes.ratingsIconsList}>
                <StarRating reviewStars={averageRating as number} />
              </div>

              {/* Ratings RbA */}
              <div className={themeData.classes.ratingsText}>
                <Text tag="h5" field={{ value: averageRating }} />
              </div>
            </>
          )}
          {themeName === 'rba' && priceLevel && priceLevel > 0 && (
            <>
              {/* Price Level RbA */}
              <div className={themeData.classes.priceLevelWrapper}>
                <PriceLevel
                  priceLevel={priceLevel}
                  priceClasses={themeData.classes.priceTextClasses}
                  priceLevelClasses={themeData.classes.priceLevelClasses}
                />
              </div>
            </>
          )}
          {themeName === 'aw' && priceLevel && priceLevel > 0 && (
            <>
              {/* Price Level AW*/}
              <div className={bazaarvoiceProductId ? themeData.classes.priceLevelWrapper : ''}>
                <PriceLevel
                  priceLevel={priceLevel}
                  priceClasses={themeData.classes.priceTextClasses}
                  priceLevelClasses={themeData.classes.priceLevelClasses}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div className="order-2 my-xxs self-stretch">
        <Link
          href={
            fields.cta1Link.value.href ||
            fields.productItem?.fields?.productDetailPageLink?.value?.href ||
            ''
          }
        >
          <a>
            {fields.primaryImage?.value?.src &&
            fields.primaryImageMobile?.value?.src &&
            Object.keys(selectedSwatchImage || {}).length > 0 ? (
              <ImagePrimary
                {...{
                  ...props,
                  ...(Object.keys(selectedSwatchImage || {}).length > 0 && {
                    fields: {
                      ...props.fields,
                      primaryImage: { value: selectedSwatchImage },
                      primaryImageMobile: { value: selectedSwatchImage },
                    },
                  }),
                }}
              />
            ) : fields.primaryImage?.value?.src && fields.primaryImageMobile?.value?.src ? (
              <ImagePrimary {...props} />
            ) : primaryImageVal || primaryImageMobileVal ? (
              <ImagePrimary
                imageLayout={'intrinsic'}
                fields={{
                  primaryImageCaption: {
                    value: '',
                  },
                  primaryImage: { value: primaryImageVal },
                  primaryImageMobile: { value: primaryImageMobileVal },
                  primaryImageMobileFocusArea:
                    fields.productItem?.fields?.primaryImageMobileFocusArea &&
                    fields.productItem?.fields?.primaryImageMobileFocusArea?.targetItem?.value.value
                      ? {
                          id: '',
                          url: '',
                          name: fields.productItem.fields.primaryImageMobileFocusArea.targetItem
                            .value.value,
                          displayName:
                            fields.productItem.fields.primaryImageMobileFocusArea.targetItem.value
                              .value,
                          fields: {
                            Value: {
                              value:
                                fields.productItem.fields.primaryImageMobileFocusArea.targetItem
                                  .value.value,
                            },
                          },
                        }
                      : {
                          id: '',
                          url: '',
                          name: 'Center',
                          displayName: 'Center',
                          fields: {
                            Value: {
                              value: 'center',
                            },
                          },
                          templateId: 'd2923fee-da4e-49be-830c-e27764dfa269',
                          templateName: 'Enum',
                        },
                }}
              />
            ) : (
              <></>
            )}
          </a>
        </Link>
      </div>
      {fields.body?.value ? (
        <BodyCopy classes={themeData.classes.body} {...props} />
      ) : (
        fields.productItem?.fields.productDescription && (
          <RichTextWrapper
            field={{ value: fields.productItem.fields.productDescription.value }}
            classes={themeData.classes.body}
          />
        )
      )}
      <ColorAndActions />
    </div>
  );
};

export default withDatasourceCheck()<ProductPreviewCardProps>(ProductPreviewCard);
